import React, { useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

interface TipsBoxProps {
  iconImage?: string;
  heading?: string;
  tip1?: string;
  tip2?: string;
  tip3?: string;
  headingone?: string;
  subheading?: string;
}

const TipsBox: React.FC<TipsBoxProps> = ({
  heading,
  iconImage,
  tip1,
  tip2,
  tip3,
  headingone,
  subheading,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const [isExpanded, setIsExpanded] = useState(isXsOrSm ? false : true);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Helper to render a consistent bullet point style
  const renderBullet = () => (
    <Box
      sx={{
        minWidth: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: "#00B3B5",
        mt: "6px",
      }}
    />
  );

  return (
    <Box
      sx={{
        backgroundColor: isXsOrSm ? "#d5fafcff" : "#FFFFFF",
        borderRadius: isXsOrSm ? "10px" : "6px",
        border: isXsOrSm ? "1px solid #00B3B5" : "",
        boxShadow: "0px 4px 18px 0px #4B465C1A;",
        padding: isXsOrSm ? "10px 12px" : "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        onClick={isXsOrSm ? handleToggleExpand : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: isXsOrSm ? "pointer" : "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {iconImage && (
            <Box
              component="img"
              src={iconImage}
              alt={heading || "icon"}
              sx={{
                width: "24px",
                height: "24px",
                borderRadius: "6px",
                padding: "4px",
                backgroundColor: "#00B3B514",
                objectFit: "contain",
                display: "block",
              }}
              onError={(e) => {
                e.currentTarget.src = "https://placehold.co/24x24/e6f7ff/1890ff?text=!";
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              lineHeight: 1.2,
              letterSpacing: "1px",
              color: isXsOrSm ? '#168794' : "#00B3B5",
              textTransform: "uppercase",
            }}
          >
            {heading}{isXsOrSm ? '' : ':'}
          </Typography>
        </Box>

        {isXsOrSm && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                lineHeight: 1.2,
                color: isExpanded ? "#6f6b7d" : isXsOrSm ? '#168794' : "#00B3B5",
              }}
            >
              {isExpanded ? "Hide" : "Show"}
            </Typography>
          </Box>
        )}
      </Box>

      {isExpanded && (
        <Box sx={{ pt: 1 }}>
          {headingone && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                // lineHeight: 2,
                gap: "10px",
                letterSpacing: "1px",
                color: isXsOrSm ? '#168794' : "#00B3B5",
              }}
            >
              {headingone}
            </Typography>
          )}
          {subheading && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize:  isXsOrSm ? "11px" : '12px',
                // lineHeight: 2,
                color: "#6f6b7d",
              }}
            >
              {subheading}
            </Typography>
          )}
          {tip1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                mb: 0,
                mt: 1
              }}
            >
              {renderBullet()}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: isXsOrSm ? "11px" : '12px',
                  color: "#6f6b7d",
                  verticalAlign: "middle",
                }}
              >
                {tip1}
              </Typography>
            </Box>
          )}

          {tip2 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              {renderBullet()}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: isXsOrSm ? "11px" : '12px',
                  color: "#6f6b7d",
                  verticalAlign: "middle",
                }}
              >
                {tip2}
              </Typography>
            </Box>
          )}

          {tip3 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              {renderBullet()}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontWeight: 400,
                  fontSize: isXsOrSm ? "11px" : '12px',
                  color: "#6f6b7d",
                  verticalAlign: "middle",
                }}
              >
                {tip3}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TipsBox;
